import {
  ListAccessories,
  ScanAccessory,
  StoreAccessory,
} from '@/services/accessoryService';
import { Accessory, StoreAccessoryRequest } from '@/types/accessory';
import { Query } from '@/types/query';
import { PaginatedResponse, Response } from '@/types/response';
import { handleError } from '@/utils/handleError';
import { AxiosError } from 'axios';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

const initialFilters: Query = {
  current_page: 1,
  per_page: 10,
  search: '',
  sort_field: 'id',
  sort_order: 'desc',
  user_id: undefined,
};

export interface AccessoryState {
  isLoading: boolean;
  accessories: Accessory[];
  accessory: Accessory | null;
  error: string | null;
  filters: Query;
  limitExceeded: boolean;
  scanAccessory: (uuid: string) => Promise<void>;
  storeAccessory: (
    accessory: StoreAccessoryRequest,
  ) => Promise<Response<Accessory>>;
  listAccessories: (
    query: Query,
  ) => Promise<Response<Accessory[] | PaginatedResponse<Accessory>>>;
  setFilters: (filters: Partial<Query>) => void;
}

export const useAccessoryStore = create<AccessoryState>()(
  immer<AccessoryState>(set => ({
    isLoading: false,
    accessories: [],
    accessory: null,
    error: null,
    filters: initialFilters,
    limitExceeded: false,

    scanAccessory: async (uuid: string) => {
      try {
        set(state => {
          state.isLoading = true;
          state.error = null;
        });
        const response = await ScanAccessory(uuid);
        set(state => {
          state.accessory = response.data.data;
        });
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.message;
          set(state => {
            state.error = errorMessage;
          });
        }
        throw error;
      } finally {
        set(state => {
          state.isLoading = false;
        });
      }
    },

    storeAccessory: async (accessory: StoreAccessoryRequest) => {
      try {
        set(state => {
          state.isLoading = true;
        });
        const { data, message } = await StoreAccessory(accessory);
        set(state => {
          state.accessory = data;
        });
        return { data, message };
      } catch (error) {
        handleError(error);
        throw error;
      } finally {
        set(state => {
          state.isLoading = false;
        });
      }
    },

    listAccessories: async (query: Query) => {
      try {
        set(state => {
          state.isLoading = true;
        });
        const response = await ListAccessories(query);
        if ('pagination' in response.data) {
          const paginatedResponse =
            response as unknown as PaginatedResponse<Accessory>;
          set(state => {
            state.accessories =
              query.current_page === 1
                ? paginatedResponse.data.data
                : [...state.accessories, ...paginatedResponse.data.data];
            state.filters = {
              ...query,
              total: paginatedResponse.data.pagination.total,
              last_page: paginatedResponse.data.pagination.last_page,
            };
            state.limitExceeded = paginatedResponse.data.meta.limit_exceeded;
          });
        } else {
          const simpleResponse = response.data as Accessory[];
          let limitExceeded = false;
          if (
            typeof response.data === 'object' &&
            response.data !== null &&
            'meta' in response.data
          ) {
            const responseWithMeta = response.data as {
              meta: { limit_exceeded: boolean };
            };
            limitExceeded = responseWithMeta.meta.limit_exceeded;
          }
          set(state => {
            state.accessories =
              query.current_page === 1
                ? simpleResponse
                : [...state.accessories, ...simpleResponse];
            state.filters = {
              ...query,
              total: simpleResponse.length,
              last_page: 1,
            };
            state.limitExceeded = limitExceeded;
          });
        }
        return response;
      } catch (error) {
        handleError(error);
        throw error;
      } finally {
        set(state => {
          state.isLoading = false;
        });
      }
    },

    setFilters: (filters: Partial<Query>) => {
      set(state => {
        state.filters = { ...state.filters, ...filters };
      });
    },
  })),
);
